import React from "react";
import { Box, Button, IconButton, Stack } from "@mui/material";
import { Add, DeleteOutline } from "@mui/icons-material";
import { CustomTextField } from "../../../../components/formsComponents";
import { MuiNumberField } from "../../../../components/formsComponents/MuiNumberField";
import { FieldLabel } from "../components/FieldLabel";
import { ACCENT } from "../BusinessOnboard.constant";
import { IVenue, VenueErrors } from "../BusinessOnboard.types";

interface IVenueStepProps {
  venues: IVenue[];
  venueErrors: VenueErrors;
  onChange: (index: number, field: keyof IVenue, value: string) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
}

const inputSx = {
  "& .MuiInputBase-root": { borderRadius: "10px" },
};

/**
 * Step 2 — venues as an inline table (Name | Seating | Floating | remove).
 * The inline layout uses horizontal space so many venues stay compact.
 */
export const VenueStep: React.FC<IVenueStepProps> = ({
  venues,
  venueErrors,
  onChange,
  onAdd,
  onRemove,
}) => {
  const multiple = venues.length > 1;

  // Column template: name (flex) | seating | floating | remove.
  // Fractions fill the full row width evenly (name wider than capacities).
  const gridTemplate = multiple
    ? "minmax(0, 2fr) minmax(0, 1.3fr) minmax(0, 1.3fr) 36px"
    : "minmax(0, 2fr) minmax(0, 1.3fr) minmax(0, 1.3fr)";

  return (
    <Stack spacing={1.5}>
      {/* Header row (hidden on very small screens where it wraps) */}
      <Box
        sx={{
          display: { xs: "none", sm: "grid" },
          gridTemplateColumns: gridTemplate,
          gap: 1,
          alignItems: "center",
          px: 0.25,
        }}
      >
        <FieldLabel label="Venue name" required noMargin />
        <FieldLabel label="Seating capacity" required noMargin />
        <FieldLabel label="Floating capacity" required noMargin />
        {multiple && <Box />}
      </Box>

      {/* Venue rows */}
      {venues.map((venue, index) => {
        const errors = venueErrors[index];
        return (
          <Box
            key={index}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: gridTemplate },
              gap: 1,
              alignItems: "flex-start",
            }}
          >
            {/* Mobile-only per-field labels appear via placeholders; on sm+ the
                header row labels apply. */}
            <CustomTextField
              placeholder="e.g. Ballroom / Hall 1"
              fullWidth
              value={venue.venue_name}
              onChange={(e) => onChange(index, "venue_name", e.target.value)}
              error={!!errors?.venue_name}
              helperText={errors?.venue_name}
              sx={inputSx}
            />
            <MuiNumberField
              name="seating_capacity"
              placeholder="Seating"
              value={venue.seating_capacity}
              onChange={(e) => onChange(index, "seating_capacity", e.target.value)}
              error={errors?.seating_capacity || ""}
              sx={inputSx}
            />
            <MuiNumberField
              name="floating_capacity"
              placeholder="Floating"
              value={venue.floating_capacity}
              onChange={(e) => onChange(index, "floating_capacity", e.target.value)}
              error={errors?.floating_capacity || ""}
              sx={inputSx}
            />
            {multiple && (
              <IconButton
                size="small"
                onClick={() => onRemove(index)}
                sx={{
                  color: "text.secondary",
                  justifySelf: { xs: "flex-end", sm: "center" },
                  mt: { xs: 0, sm: 0 },
                }}
              >
                <DeleteOutline fontSize="medium" color="error" />
              </IconButton>
            )}
          </Box>
        );
      })}

      <Button
        onClick={onAdd}
        startIcon={<Add />}
        variant="outlined"
        sx={{
          alignSelf: "flex-start",
          borderRadius: "10px",
          fontWeight: 700,
          textTransform: "none",
          color: ACCENT.main,
          borderColor: ACCENT.main,
          "&:hover": { borderColor: ACCENT.dark, backgroundColor: ACCENT.soft },
        }}
      >
        Add another venue
      </Button>
    </Stack>
  );
};
